import { Injectable, inject } from '@angular/core';
import { ArticleRequest } from '@core/interfaces/article-request.interface';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import {
  BehaviorSubject,
  Observable,
  catchError,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  ArticleProperty,
  ArticleType,
  Auction,
  CertificateType,
} from '../interfaces/auction.interface';

@Injectable({
  providedIn: 'root',
})
export class ArticleService {
  private loaded = false;

  public readonly favorites: BehaviorSubject<Set<number>> = new BehaviorSubject(
    new Set<number>()
  );

  authService = inject(AuthService);

  constructor(private _api: ApiService) {}

  getTypes(): Observable<ArticleType[]> {
    return this._api.request(ApiServiceHolder.article.getTypes());
  }

  getAllCertificateTypes(): Observable<CertificateType[]> {
    return this._api.request(ApiServiceHolder.article.getAllCertificateTypes());
  }

  getProperties(iDArticleType: number): Observable<ArticleProperty[]> {
    return this._api.request(
      ApiServiceHolder.article.getProperties(iDArticleType)
    );
  }

  getArticleByCertificate(certificate: string): Observable<Auction> {
    return this._api.request(
      ApiServiceHolder.article.getArticleByCertificate(certificate)
    );
  }

  isFavorite(id: number) {
    if (!this.loaded) {
      this.loadFavorites();
    }

    return this.favorites.asObservable().pipe(
      switchMap((favorites) => {
        return of(favorites.has(id));
      })
    );
  }

  save(item: ArticleRequest): Observable<void> {
    return this._api
      .request<any>(ApiServiceHolder.starredarticle.save(item))
      .pipe(
        tap(() => {
          this.favorites.next(
            new Set([...this.favorites.value, item.idArticle])
          );
          return of();
        }),
        catchError(() => {
          this.loadFavorites();
          return of();
        })
      );
  }

  delete(item: ArticleRequest): Observable<void> {
    return this._api
      .request<void>(ApiServiceHolder.starredarticle.delete(item))
      .pipe(
        tap(() => {
          this.favorites.next(
            new Set(
              [...this.favorites.value].filter((id) => id !== item.idArticle)
            )
          );
          return of();
        }),
        catchError(() => {
          this.loadFavorites();
          return of();
        })
      );
  }

  loadFavorites() {
    this.loaded = true;
    if (!this.authService.isAuthenticated()) {
      setTimeout(() => {
        if (this.authService.isAuthenticated()) {
          this.loadFavorites();
        }
      }, 1000);
      return;
    }

    this._api
      .request<number[]>(ApiServiceHolder.starredarticle.getAll())
      .subscribe({
        next: (favorites) => {
          this.favorites.next(new Set(favorites));
          this.loaded = true;
        },
        error: () => {
          this.loaded = false;
        },
      });
  }
}
